/**
 * Per-CEFR-level progress grid. Shows all six levels side by side so
 * the user can see where they stand across the whole ladder — answers
 * "why am I still A1 if I'm speaking full German?" by showing that
 * even a fast learner only has ~40-50% of any single level's curriculum
 * actively mastered.
 */
import type { LevelBreakdown } from "../../api";

const LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];

export default function CefrLevelGrid({
  breakdowns,
  currentLevel,
}: {
  breakdowns: LevelBreakdown[];
  currentLevel: string;
}) {
  if (!breakdowns || breakdowns.length === 0) return null;

  const byLevel = new Map(breakdowns.map((b) => [b.level, b]));
  const currentIdx = LEVELS.indexOf(currentLevel);

  return (
    <div className="card">
      <div className="mb-5">
        <div className="text-xs text-ink-faint uppercase tracking-[0.14em]">
          progression across levels
        </div>
        <h3 className="font-serif text-2xl mt-1">
          Where you stand on the CEFR ladder
        </h3>
        <p className="mt-2 text-xs text-ink-soft leading-relaxed max-w-2xl">
          Each card shows how much of that level's curriculum you've
          mastered. Your CURRENT level (<span className="italic-accent">{currentLevel}</span>)
          is highlighted. Mastering 70% of a level promotes you to the next.
          Even fast learners who feel fluent often have only 40-60% of any
          single level's curriculum truly consolidated — because real German
          spreads across many levels at once.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        {LEVELS.map((lvl, idx) => {
          const b = byLevel.get(lvl);
          if (!b) return null;
          const pct = Math.round(b.vocab_pct * 100);
          const isCurrent = lvl === currentLevel;
          const isPast = idx < currentIdx;
          const isFuture = idx > currentIdx;
          return (
            <div
              key={lvl}
              className={
                "relative rounded-md p-3 border transition " +
                (isCurrent
                  ? "bg-terracotta/10 border-terracotta/40"
                  : isPast
                    ? "bg-paper-warm/30 border-ink-faint/15"
                    : "bg-paper border-ink-faint/15")
              }
            >
              <div className="flex items-baseline justify-between">
                <div
                  className={
                    "font-serif text-xl " +
                    (isCurrent ? "italic-accent" : "text-ink")
                  }
                >
                  {lvl}
                </div>
                {isCurrent ? (
                  <span className="text-[9px] uppercase tracking-wider bg-terracotta text-paper px-1.5 py-0.5 rounded">
                    now
                  </span>
                ) : null}
              </div>
              {/* progress bar */}
              <div className="mt-2 h-1.5 bg-ink-faint/15 rounded-full overflow-hidden">
                <div
                  className={
                    "h-full rounded-full " +
                    (pct >= 70
                      ? "bg-moss"
                      : pct >= 40
                        ? "bg-terracotta"
                        : "bg-ink-faint/50")
                  }
                  style={{ width: `${Math.min(100, pct)}%` }}
                />
              </div>
              <div className="mt-2 text-[11px] text-ink-soft">
                <div className="font-serif text-base text-ink">
                  {pct}
                  <span className="text-xs text-ink-faint">%</span>
                </div>
                <div className="text-[10px] text-ink-faint mt-0.5">
                  {b.vocab_active}/{b.vocab_total} words
                </div>
              </div>
              {isFuture && pct === 0 ? (
                <div className="mt-1 text-[9px] text-ink-faint uppercase tracking-wider">
                  not started
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="mt-5 text-[11px] text-ink-soft leading-relaxed">
        <div className="flex items-center gap-4 flex-wrap">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-moss" />
            ≥70% — promoted via Path A
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-terracotta" />
            40-69% — Path B compensated promotion possible
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-ink-faint/50" />
            &lt;40% — foundation still building
          </span>
        </div>
      </div>
    </div>
  );
}
